import React, { useMemo } from "react";
import styled, { css as styledCss } from "styled-components";
import { css } from "@styled-system/css";
import { themeGet } from "@styled-system/theme-get";
import PropTypes from "prop-types";
import Icon from "../Icon";
import Popover from "../Popover";
import { applySmallScreenCss } from "./styles/smallScreen";
import sharedNavItemHoverStyles, {
  sharedNavItemActiveStylesProps,
  sharedMenuItemContainerStyles,
  hoverForeground,
} from "./styles/sharedHoverStyles";

// Use constants for NavItem consistency and maintainability
const ICON_COLUMN_WIDTH = "12px";
const MOBILE_ITEM_WIDTH = "30px";
const ITEM_HEIGHT = "30px";

// Constrain icon width to prevent alignment issues with variable-width icons
const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${ICON_COLUMN_WIDTH};
  flex-shrink: 0;
`;

const sharedNavItemStyles = (props) => styledCss`
  cursor: pointer;
  padding: 4px;
  text-decoration: none;
  border-radius: ${themeGet("radii.2")};
  width: ${MOBILE_ITEM_WIDTH};
  height: ${ITEM_HEIGHT};
  position: relative;
  border: none;
  path {
    fill: ${themeGet("colors.subText")};
  }
  font-family: ${themeGet("fonts.main")};

  ${
    props.isExpanded
      ? styledCss`
        display: grid;
        grid-template-columns: ${ICON_COLUMN_WIDTH};
        place-items: center;
      `
      : styledCss`
        display: flex;
        align-items: center;
        justify-content: center;
      `
  }
  background-color: transparent;
  font-size: 1.4rem;
  font-weight: ${themeGet("fontWeights.1")};

  ${sharedNavItemHoverStyles}

  ${(props) =>
    applySmallScreenCss(
      props,
      styledCss`
        width: ${MOBILE_ITEM_WIDTH};
        height: ${ITEM_HEIGHT};
        display: flex;
        align-items: center;
        justify-content: center;
        -webkit-tap-highlight-color: transparent;
        &:active {
          background-color: transparent;
        }
      `
    )}
  &:focus {
    outline: 0;
  }
`;

const expandedNavItemStyles = (props) => styledCss`
  display: grid;
  width: 100%;
  height: ${ITEM_HEIGHT};
  grid-template-columns: ${ICON_COLUMN_WIDTH} 1fr;
  gap: 4px;
  justify-items: start;
  align-items: center;
  padding: 4px 9px;
  
  .nav-item-text {
    font-size: ${themeGet("fontSizes.1")};
    line-height: 1;
    padding-left: 4px;
    font-weight: ${themeGet("fontWeights.1")};
    color: ${themeGet("colors.bodyText")};
    text-align: left;
    justify-self: start;
  }

  /* Turn text and icon to the hover foreground on hover (white in dark, brand
     blue in light), even for active items */
  &:hover .nav-item-text,
  &:focus .nav-item-text {
    color: ${hoverForeground};
  }
  
  &:hover path,
  &:focus path {
    fill: ${hoverForeground};
  }

  ${(props) =>
    applySmallScreenCss(
      props,
      styledCss`
        width: ${MOBILE_ITEM_WIDTH};
        height: ${ITEM_HEIGHT};
        display: flex;
        align-items: center;
        justify-content: center;
      `
    )}
`;

const variantActiveStyles = {
  expandableItem: () => styledCss`
  color: ${themeGet("colors.bodyText")};

  path {
    fill: ${themeGet("colors.bodyText")};
  }
  &:hover .nav-item-text,
  &:focus .nav-item-text {
    color: ${hoverForeground};
  }
    
  /* Keep icon grey on mobile for expandable items with blue line */
  path {
    fill: ${themeGet("colors.bodyText")};
  }
    
  &:hover path,
  &:focus path {
    fill: ${themeGet("colors.bodyText")};
  }
`,
  default: styledCss`
    ${(props) => sharedNavItemActiveStylesProps(props)}
  `,
};

const getActiveStyles = (props) => {
  if (!props.active) return "";

  if (props["aria-expanded"]) {
    return variantActiveStyles.expandableItem(props);
  }

  return variantActiveStyles.default;
};

const SideNavItemLink = styled.div`
  width: 100%;
  & > a {
    ${(props) => sharedNavItemStyles(props)}
    ${(props) => (props.isExpanded ? expandedNavItemStyles(props) : "")}
    ${getActiveStyles}

    /* Remove backgrounds - let wrapper handle them */
    background-color: transparent !important;

    &:hover,
    &:focus,
    &:active {
      background-color: transparent !important;
    }
  }
`;

const SideNavItem = styled("button")`
  ${(props) => sharedNavItemStyles(props)}
  ${(props) => (props.isExpanded ? expandedNavItemStyles(props) : "")}
  ${getActiveStyles}

  /* Remove backgrounds - let wrapper handle them */
  background-color: transparent !important;

  &:hover,
  &:focus,
  &:active {
    background-color: transparent !important;
  }
`;

const BadgeNumber = styled("div")((props) =>
  css({
    position: "absolute",
    height: "16px",
    width: "16px",
    bg: themeGet("colors.danger")(props),
    fontSize: "1rem",
    fontWeight: themeGet("fontWeights.2")(props),
    color: themeGet("colors.white")(props),
    borderRadius: "50%",
    top: "50%",
    right: "0",
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  })
);

const BadgeDot = styled("div")((props) =>
  css({
    position: "absolute",
    height: "8px",
    width: "8px",
    bg: themeGet("colors.primary")(props),
    borderRadius: "50%",
    top: "50%",
    right: "0",
    transform: "translateY(-50%)",
  })
);

const SideNavItemWrapper = styled.div`
  ${sharedMenuItemContainerStyles}
  position: relative;

  &:nth-child(1 of .bottom-aligned) {
    margin-top: ${(props) => props.bottomAligned && "auto"};
  }

  /* Override shared container's active background for expandable items only */
  ${(props) =>
    props.isExpandable &&
    styledCss`
    &.active,
    &:has(a.active),
    &:has(button.active) {
      background-color: transparent;
    }
    
    /* Remove hover backgrounds for expandable items */
    &:hover {
      background-color: transparent;
    }
  `}

  /* Apply blue background ONLY for active link items, NOT expandable items */
  /* Desktop: full background */
  ${(props) =>
    props.isActive &&
    props.isLink &&
    !props.isExpandable &&
    styledCss`
    background-color: ${themeGet("colors.navActiveBackground")};
  `}

  ${(props) =>
    applySmallScreenCss(
      props,
      props.isActive && props.isLink && !props.isExpandable
        ? styledCss`
            background-color: ${themeGet("colors.navActiveBackground")};
            path {
              fill: ${themeGet("colors.bodyText")};
            }
            path[stroke] {
              stroke: ${themeGet("colors.bodyText")};
            }
            &:hover path,
            &:focus path,
            &:active path {
              fill: ${themeGet("colors.bodyText")};
            }
            &:hover path[stroke],
            &:focus path[stroke],
            &:active path[stroke] {
              stroke: ${themeGet("colors.bodyText")};
            }
          `
        : ""
    )}

  /* Blue vertical line for active expandable (component) items */
  ${(props) =>
    props.isActive &&
    props.isExpandable &&
    styledCss`
    &::after {
      content: "";
      position: absolute;
      right: -9px;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: ${themeGet("colors.primary")};
      z-index: 5;
    }
  `}

  ${(props) =>
    applySmallScreenCss(
      props,
      props.isActive && props.isExpandable
        ? styledCss`
            &::after {
              content: "";
              position: absolute;
              top: 0;
              left: 50%;
              width: ${MOBILE_ITEM_WIDTH};
              height: 0;
              border-top: 3px solid ${themeGet("colors.primary")};
              transform: translate(-50%, -10px);
              z-index: 10;
            }
          `
        : ""
    )}
`;

/**
 * NavItem - A navigation item component for the SideNav
 *
 * Renders either a button or link-based navigation item with support for:
 * - Icons with optional badges
 * - Expandable/collapsible states
 * - Active state styling
 * - Responsive behavior
 * - Accessibility features
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Navigation item data
 * @param {React.ElementType} [props.Component] - Component to render for link items
 * @param {boolean} props.isActive - Whether the item is currently active
 * @param {Function} props.handleItemClick - Click handler function
 * @param {Object} props.navItemRefs - Ref object for managing item references
 * @param {boolean} props.isSmallScreen - Whether currently on a small screen
 * @param {boolean} props.isExpanded - Whether the navigation is expanded
 * @returns {JSX.Element} Rendered navigation item
 */
const NavItem = ({
  item,
  Component,
  isActive,
  handleItemClick,
  navItemRefs,
  isSmallScreen,
  isExpanded,
}) => {
  const renderedNavItem = useMemo(() => {
    const accessibilityProps = {
      ...(item.actionType === "component" && {
        "aria-expanded": isActive ? "true" : "false",
      }),
      "aria-label": item.name,
    };

    if (item.actionType === "link") {
      return (
        <SideNavItemLink
          key={item.index}
          active={isActive}
          bottomAligned={item.bottomAligned}
          isExpanded={isExpanded}
          isSmallScreen={isSmallScreen}
          onClick={() => handleItemClick(item)}
          ref={(el) => (navItemRefs.current[item.index] = el)}
        >
          <Component item={item}>
            <IconWrapper iconName={item.iconName}>
              <Icon icon={["far", item.iconName]} fixedWidth />
            </IconWrapper>
            {isExpanded && !isSmallScreen && (
              <span className="nav-item-text">{item.name}</span>
            )}
          </Component>
        </SideNavItemLink>
      );
    } else {
      return (
        <SideNavItem
          key={item.index}
          active={isActive}
          onClick={() => handleItemClick(item)}
          bottomAligned={item.bottomAligned}
          isExpanded={isExpanded}
          isSmallScreen={isSmallScreen}
          {...accessibilityProps}
          ref={(el) => (navItemRefs.current[item.index] = el)}
        >
          {item.badgeNumber && (
            <BadgeNumber isExpanded={isExpanded}>
              {item.badgeNumber}
            </BadgeNumber>
          )}
          {item.badgeDot && <BadgeDot isExpanded={isExpanded} />}
          <IconWrapper iconName={item.iconName}>
            <Icon icon={["far", item.iconName]} fixedWidth />
          </IconWrapper>
          {isExpanded && !isSmallScreen && (
            <span className="nav-item-text">{item.name}</span>
          )}
        </SideNavItem>
      );
    }
  }, [item, isActive, isExpanded, isSmallScreen, handleItemClick, navItemRefs]);

  return isSmallScreen || !isExpanded ? (
    <SideNavItemWrapper
      className={item.bottomAligned && "bottom-aligned"}
      bottomAligned={item.bottomAligned}
      isActive={isActive}
      isLink={item.actionType === "link"}
      isExpandable={item.actionType === "component"}
      isSmallScreen={isSmallScreen}
    >
      <Popover
        text={item.name}
        direction={isSmallScreen ? "top" : "right"}
        textAlign="center"
        width="fit-content"
        tabIndex="-1"
      >
        {renderedNavItem}
      </Popover>
    </SideNavItemWrapper>
  ) : (
    <SideNavItemWrapper
      className={item.bottomAligned && "bottom-aligned"}
      bottomAligned={item.bottomAligned}
      isActive={isActive}
      isLink={item.actionType === "link"}
      isExpandable={item.actionType === "component"}
      isSmallScreen={isSmallScreen}
    >
      {renderedNavItem}
    </SideNavItemWrapper>
  );
};

NavItem.propTypes = {
  item: PropTypes.shape({
    index: PropTypes.number,
    iconName: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    actionType: PropTypes.oneOf(["component", "link", "button"]).isRequired,
    bottomAligned: PropTypes.bool,
    hide: PropTypes.bool,
    component: PropTypes.elementType,
    onClick: PropTypes.func,
    isActive: PropTypes.bool,
    badgeNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    badgeDot: PropTypes.bool,
  }).isRequired,
  Component: PropTypes.elementType,
  isActive: PropTypes.bool,
  handleItemClick: PropTypes.func.isRequired,
  navItemRefs: PropTypes.object,
  isSmallScreen: PropTypes.bool,
  isExpanded: PropTypes.bool,
};

export default NavItem;
